import React, { useCallback, useMemo, useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Price } from '@fiverr-private/portfolio/src/types/types';
import { PortfolioSlider } from '@fiverr-private/portfolio';
import { translate } from '@fiverr-private/i18n-react';
import { FileType } from '@fiverr-private/bianka_js/protobuf/bianka/enums/assets_sharing/asset/file_type';
import { PortfolioProjectWithTags } from '../../types';
import { CustomOrderButton } from '../CustomOrder';
import { useBI } from '../BIContext';
import { DetailsSection } from './DetailsSection';
import { getStartingPriceForFilteredProjects, PortfolioProjectPrice } from './utils';
import { DETAILS_SECTION_WIDTH } from './consts';

interface PortfolioSliderProps {
    portfolioProjects: PortfolioProjectWithTags[];
    selectedSkillName: string;
    userName: string;
    avatarSrc?: string | null;
    userDisplayName?: string | null;
    allProjectsPricing?: PortfolioProjectPrice;
    selectedServiceId: string;
}

export const PortfolioSliderSection = ({
    portfolioProjects,
    selectedSkillName,
    userName,
    avatarSrc,
    userDisplayName,
    allProjectsPricing,
    selectedServiceId,
}: PortfolioSliderProps) => {
    const {
        reportPortfolioCardClicked,
        reportSliderScrollClicked,
        reportMediaPlayButtonClicked,
        reportGetQuoteButtonClicked,
    } = useBI();
    const [selectedProjectId, setSelectedProjectId] = useState(portfolioProjects[0]?.id);

    const startingPrice = useMemo(
        () => getStartingPriceForFilteredProjects(portfolioProjects, allProjectsPricing),
        [portfolioProjects, allProjectsPricing]
    ) as Price;

    const handleCustomOrderClick = useCallback(() => {
        reportGetQuoteButtonClicked({
            label: translate('seller_page_perseus.professions_page.main_content.services_offered.get_quote'),
            additionalDataMessage: {
                startingPrice: startingPrice?.amount,
                projectId: selectedProjectId,
                serviceId: selectedServiceId,
            },
        });
    }, [selectedProjectId, selectedServiceId, reportGetQuoteButtonClicked, startingPrice]);

    const handleMediaClick = useCallback(
        (projectId: string, mediaType: FileType) => {
            reportMediaPlayButtonClicked({
                label: '',
                additionalDataMessage: { projectId, serviceId: selectedServiceId, mediaType },
            });
        },
        [reportMediaPlayButtonClicked, selectedServiceId]
    );

    const handleSlideChange = useCallback(
        (projectId: string) => {
            reportSliderScrollClicked({
                label: '',
                additionalDataMessage: { projectId, serviceId: selectedServiceId },
            });
            setSelectedProjectId(projectId);
        },
        [reportSliderScrollClicked, selectedServiceId]
    );

    const handleSlideClick = useCallback(
        (projectId: string) => {
            reportPortfolioCardClicked({
                label: '',
                additionalDataMessage: { projectId, serviceId: selectedServiceId },
            });
        },
        [reportPortfolioCardClicked, selectedServiceId]
    );

    return (
        <Container>
            <Stack
                borderWidth={{ default: 0, md: 'sm' }}
                justifyContent="flexStart"
                gap="8"
                borderColor="grey_400"
                borderStyle="solid"
                borderRadius="2xl"
                paddingX={{
                    default: '0',
                    md: '8',
                }}
                paddingY={{
                    default: '0',
                    md: '6',
                }}
                maxHeight={{
                    default: 'auto',
                    md: '306px',
                }}
            >
                <DetailsSection
                    selectedSkillName={selectedSkillName}
                    onCustomOrderClick={handleCustomOrderClick}
                    startingPrice={startingPrice}
                />
                <Container
                    maxWidth={{
                        default: '100%',
                        md: `calc(100% - ${DETAILS_SECTION_WIDTH + 32 * 2}px)`,
                    }}
                >
                    <PortfolioSlider
                        projects={portfolioProjects}
                        userName={userName}
                        avatarSrc={avatarSrc}
                        userDisplayName={userDisplayName}
                        startingPrice={startingPrice}
                        selectedSkill={selectedSkillName}
                        onMediaClick={handleMediaClick}
                        onSlideChange={handleSlideChange}
                        onSlideClick={handleSlideClick}
                    />
                </Container>
            </Stack>
            <Stack
                width="100%"
                marginTop={{
                    default: '6',
                    md: '0',
                }}
                visibility={{
                    default: 'visible',
                    md: 'hidden',
                }}
                height={{
                    default: 'auto',
                    md: 0,
                }}
            >
                <CustomOrderButton
                    fullWidth={true}
                    buttonText="seller_page_perseus.portfolio_slider.get_custom_offer"
                    onClick={handleCustomOrderClick}
                />
            </Stack>
        </Container>
    );
};
